import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Calendar, Plus, AlertCircle, CheckCircle, XCircle, Clock, RefreshCw, HelpCircle, Edit2, Trash2, X } from 'lucide-react';
import {
  useAppointments,
  useAppointmentsStore,
  AppointmentCalendar,
  AppointmentModal,
} from '../features/appointments';
import { formatDateToday } from '../features/appointments/services/appointmentService';

const Appointments = () => {
  const { t } = useTranslation('appointments');

  const {
    enrichedAppointments,
    pendingAppointments,
    customers, employees, services,
    loading, error, fetchErrors,
    countByStatus, countToday,
    formatDateForDisplay,
    getPendingDisplayName, getPendingServiceName,
    handleOpenModal, handleEditAppointment, handleSaveAppointment,
    handleConfirmDelete,
    handleApproveAppointment, handleRejectAppointment,
    handleCompleteAppointment, handleConfirmComplete,
    handleDaySelect, handleRefresh,
  } = useAppointments();

  // ── Store ──────────────────────────────────────────────────
  const viewedMonth        = useAppointmentsStore((s) => s.viewedMonth);
  const setViewedMonth     = useAppointmentsStore((s) => s.setViewedMonth);
  const calendarViewMode   = useAppointmentsStore((s) => s.calendarViewMode);
  const setCalendarViewMode = useAppointmentsStore((s) => s.setCalendarViewMode);
  const showTooltip        = useAppointmentsStore((s) => s.showTooltip);
  const setShowTooltip     = useAppointmentsStore((s) => s.setShowTooltip);
  const selectedDate       = useAppointmentsStore((s) => s.selectedDate);
  const selectedAppointments = useAppointmentsStore((s) => s.selectedAppointments);
  const clearSelectedDate  = useAppointmentsStore((s) => s.clearSelectedDate);
  const isModalOpen        = useAppointmentsStore((s) => s.isModalOpen);
  const closeModal         = useAppointmentsStore((s) => s.closeModal);
  const isEditing          = useAppointmentsStore((s) => s.isEditing);
  const appointmentForm    = useAppointmentsStore((s) => s.appointmentForm);
  const setAppointmentForm = useAppointmentsStore((s) => s.setAppointmentForm);
  const selectedServices   = useAppointmentsStore((s) => s.selectedServices);
  const setSelectedServices = useAppointmentsStore((s) => s.setSelectedServices);
  const isSaving           = useAppointmentsStore((s) => s.isSaving);
  const deleteConfirmOpen  = useAppointmentsStore((s) => s.deleteConfirmOpen);
  const openDeleteConfirm  = useAppointmentsStore((s) => s.openDeleteConfirm);
  const closeDeleteConfirm = useAppointmentsStore((s) => s.closeDeleteConfirm);
  const billConfirmOpen    = useAppointmentsStore((s) => s.billConfirmOpen);
  const closeBillConfirm   = useAppointmentsStore((s) => s.closeBillConfirm);
  const actionLoading      = useAppointmentsStore((s) => s.actionLoading);

  const statusConfig = {
    pending:   { label: t('status.pending'),   bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', icon: AlertCircle },
    approved:  { label: t('status.approved'),  bgColor: 'bg-blue-100',   textColor: 'text-blue-800',   icon: CheckCircle },
    canceled:  { label: t('status.canceled'),  bgColor: 'bg-red-100',    textColor: 'text-red-800',    icon: XCircle     },
    completed: { label: t('status.completed'), bgColor: 'bg-green-100',  textColor: 'text-green-800',  icon: CheckCircle },
    deleted:   { label: t('status.deleted'),   bgColor: 'bg-gray-100',   textColor: 'text-gray-800',   icon: XCircle     },
  };

  const today = formatDateToday();

  // Tablo: seçili gün varsa o günün randevuları, yoksa bugün ve sonrası
  const tableAppointments = selectedDate
    ? selectedAppointments
    : enrichedAppointments
        .filter((a) => a.status !== 'deleted' && a.status !== 'canceled' && a.date >= today)
        .sort((a, b) => {
          if (a.date !== b.date) return a.date < b.date ? -1 : 1;
          return (a.time || '').localeCompare(b.time || '');
        })
        .slice(0, 10);

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick draggable pauseOnHover />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Başlık */}
        <div className="flex flex-col mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Calendar className="h-6 w-6 text-blue-500" />
              {t('pageTitle')}
            </h1>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 animate-pulse relative">
              {t('newFeaturesBadge')}
              <span className="absolute inset-0 rounded-full bg-blue-200 animate-ping opacity-75"></span>
            </span>
          </div>
          <p className="text-gray-600 mt-1">{t('subtitle')}</p>
        </div>

        {/* Fetch hataları */}
        {fetchErrors.length > 0 && (
          <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-yellow-400 flex-shrink-0" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">{t('fetchErrors.title')}</h3>
                <p className="text-sm text-yellow-700 mt-1">{t('fetchErrors.description')}</p>
                <ul className="list-disc pl-5 space-y-1 mt-2 text-sm text-yellow-700">
                  {fetchErrors.slice(0, 3).map((e, i) => <li key={i}>{e}</li>)}
                  {fetchErrors.length > 3 && <li>{t('fetchErrors.moreErrors', { count: fetchErrors.length - 3 })}</li>}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Hızlı İşlemler */}
        <div className="bg-white shadow-md rounded-lg p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center justify-between md:justify-start gap-3">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-800">{t('quickActions.title')}</h2>
                <div className="relative">
                  <button onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}
                    onClick={() => setShowTooltip(!showTooltip)}
                    className="text-gray-400 hover:text-blue-500 transition-colors focus:outline-none"
                    aria-label={t('tooltip.ariaLabel')}>
                    <HelpCircle className="h-5 w-5" />
                  </button>
                  {showTooltip && (
                    <div className="absolute left-0 top-8 z-50 w-80 md:w-96 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="h-5 w-5 text-blue-500" />
                        <p className="text-gray-800 font-semibold text-sm">{t('tooltip.title')}</p>
                      </div>
                      <div className="space-y-2">
                        {[
                          { color: 'blue-500',   title: t('tooltip.calendarTitle'),  desc: t('tooltip.calendarDesc') },
                          { color: 'purple-500', title: t('tooltip.viewTitle'),      desc: t('tooltip.viewDesc') },
                          { color: 'green-500',  title: t('tooltip.completeTitle'),  desc: t('tooltip.completeDesc') },
                          { color: 'cyan-500',   title: t('tooltip.navTitle'),       desc: t('tooltip.navDesc') },
                        ].map(({ color, title, desc }) => (
                          <div key={title} className="flex items-start gap-2">
                            <div className={`mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-${color}`} />
                            <p className="text-gray-600 text-xs leading-relaxed">
                              <span className="font-medium text-gray-700">{title}</span> {desc}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="absolute -top-2 left-4 w-4 h-4 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
                    </div>
                  )}
                </div>
              </div>
              <button onClick={handleOpenModal}
                className="flex items-center bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-3 py-2 rounded-md text-sm hover:from-cyan-600 hover:to-blue-600 transition-colors">
                <Plus className="h-4 w-4 mr-1" />
                {t('quickActions.newAppointment')}
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
              <div className="flex items-center justify-between p-2 md:p-3 bg-gray-50 rounded-md border border-gray-200">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-gray-500 mr-1 md:mr-2" />
                  <span className="text-xs md:text-sm">{t('quickActions.today')}</span>
                </div>
                <span className="bg-blue-100 text-blue-800 text-xs font-semibold py-1 px-2 rounded-full">{countToday()}</span>
              </div>
              <div className="flex items-center justify-between p-2 md:p-3 bg-gray-50 rounded-md border border-gray-200">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 text-yellow-500 mr-1 md:mr-2" />
                  <span className="text-xs md:text-sm">{t('quickActions.pending')}</span>
                </div>
                <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold py-1 px-2 rounded-full">{countByStatus('pending')}</span>
              </div>
              <div className="flex items-center justify-between p-2 md:p-3 bg-gray-50 rounded-md border border-gray-200">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-1 md:mr-2" />
                  <span className="text-xs md:text-sm">{t('quickActions.completed')}</span>
                </div>
                <span className="bg-green-100 text-green-800 text-xs font-semibold py-1 px-2 rounded-full">{countByStatus('completed')}</span>
              </div>
              <button onClick={handleRefresh}
                className="flex items-center justify-center p-2 md:p-3 bg-gray-100 hover:bg-gray-200 rounded-md text-xs md:text-sm text-gray-700 transition-colors border border-gray-200">
                <RefreshCw className="h-4 w-4 mr-1" />
                {t('quickActions.refresh')}
              </button>
            </div>
          </div>
        </div>

        {/* Bekleyen Randevular Paneli */}
        {pendingAppointments.length > 0 && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-amber-100 border-b border-amber-200">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <span className="font-semibold text-amber-800">{t('pendingPanel.title')}</span>
                <span className="bg-amber-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">{pendingAppointments.length}</span>
              </div>
            </div>
            <div className="divide-y divide-amber-100">
              {pendingAppointments.map((appt) => (
                <div key={appt.id} className="flex items-center justify-between px-4 py-3 hover:bg-amber-100/50 transition-colors">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${appt.source === 'guest' ? 'bg-purple-400' : 'bg-amber-400'}`} />
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">
                        {getPendingDisplayName(appt)}
                        {appt.source === 'guest' && (
                          <span className="ml-1.5 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">Online</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {getPendingServiceName(appt)} • {appt.date} {appt.time && appt.time}
                        {appt.guestPhone && ` • ${appt.guestPhone}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-3 flex-shrink-0">
                    <button onClick={() => handleApproveAppointment(appt)} disabled={actionLoading !== null}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50">
                      {actionLoading === appt.id + '_approve'
                        ? <RefreshCw className="h-3 w-3 animate-spin" />
                        : <CheckCircle className="h-3 w-3" />}
                      <span>{t('pendingPanel.approve')}</span>
                    </button>
                    <button onClick={() => handleRejectAppointment(appt)} disabled={actionLoading !== null}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50">
                      {actionLoading === appt.id + '_reject'
                        ? <RefreshCw className="h-3 w-3 animate-spin" />
                        : <XCircle className="h-3 w-3" />}
                      <span>{t('pendingPanel.reject')}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Takvim */}
        <div className="mb-6">
          <AppointmentCalendar
            appointments={enrichedAppointments}
            onDaySelect={handleDaySelect}
            onMonthChange={(newDate) => setViewedMonth(newDate)}
            onViewModeChange={(newMode, currentDate) => {
              setCalendarViewMode(newMode);
              setViewedMonth(currentDate);
            }}
          />
        </div>

        {/* Randevu Tablosu */}
        <div className="mt-6 bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">
              {selectedDate
                ? t('upcoming.dateTitle', { date: formatDateForDisplay(selectedDate) })
                : t('upcoming.title')}
            </h2>
            {selectedDate && (
              <button onClick={clearSelectedDate} className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                <RefreshCw className="h-3 w-3 mr-1" />
                {t('upcoming.showAll')}
              </button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm rounded-md text-blue-800 bg-blue-100">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('upcoming.loading')}
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 my-4">
              <div className="flex">
                <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                <p className="ml-3 text-sm text-red-700">{error}</p>
              </div>
            </div>
          ) : tableAppointments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[t('table.dateTime'), t('table.customer'), t('table.services'), t('table.status'), t('table.employee'), t('table.payment'), t('table.actions')].map((h, i) => (
                      <th key={i} scope="col" className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${i === 6 ? 'text-right' : 'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tableAppointments.map((appt) => {
                    const cfg = statusConfig[appt.status] || statusConfig.pending;
                    const StatusIcon = cfg.icon;
                    return (
                      <tr key={appt.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{appt.date || t('table.unknownDate')}</div>
                          <div className="text-sm text-gray-500">{appt.time || t('table.unknownTime')}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{appt.customerName}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-wrap gap-1">
                            {appt.services?.length > 0
                              ? appt.services.map((s, i) => (
                                  <span key={`${s.id}-${i}`} className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                    {s.name} {s.sessions > 1 ? `(${s.sessions})` : ''}
                                  </span>
                                ))
                              : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">{appt.serviceName}</span>
                            }
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${cfg.bgColor} ${cfg.textColor}`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {cfg.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{appt.employeeName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{appt.paymentMethod || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {appt.status === 'approved' && (
                            <button onClick={() => handleCompleteAppointment(appt)} disabled={actionLoading !== null}
                              className="text-green-600 hover:text-green-900 mr-3 disabled:opacity-50 disabled:cursor-not-allowed" title="Tamamla">
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}
                          <button onClick={() => handleEditAppointment(appt)} className="text-blue-600 hover:text-blue-900 mr-3">
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button onClick={() => openDeleteConfirm(appt)} className="text-red-600 hover:text-red-900">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">
              {selectedDate
                ? t('upcoming.emptyForDate', { date: formatDateForDisplay(selectedDate) })
                : t('upcoming.emptyUpcoming')}
            </p>
          )}
        </div>

        {/* AppointmentModal */}
        <AppointmentModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSave={handleSaveAppointment}
          isEditing={isEditing}
          appointmentForm={appointmentForm}
          setAppointmentForm={setAppointmentForm}
          customers={customers}
          employees={employees}
          services={services}
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          isSaving={isSaving}
        />

        {/* Silme Onay Dialog */}
        {deleteConfirmOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('deleteConfirm.title')}</h3>
              <p className="text-gray-600 mb-6">
                {t('deleteConfirm.message')}
                <span className="text-red-500 block mt-1 text-sm">{t('deleteConfirm.warning')}</span>
              </p>
              <div className="flex justify-end gap-3">
                <button onClick={closeDeleteConfirm}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 border border-gray-300 rounded-md">
                  {t('deleteConfirm.cancel')}
                </button>
                <button onClick={handleConfirmDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md">
                  {t('deleteConfirm.delete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Adisyon Onay Dialog */}
        {billConfirmOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t('billConfirm.title')}</h3>
                <button onClick={closeBillConfirm} disabled={actionLoading !== null}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={t('billConfirm.cancel')} aria-label={t('billConfirm.cancel')}>
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-gray-600 mb-6">{t('billConfirm.message')}</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => handleConfirmComplete(false)} disabled={actionLoading !== null}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed">
                  {t('billConfirm.justComplete')}
                </button>
                <button onClick={() => handleConfirmComplete(true)} disabled={actionLoading !== null}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed">
                  {t('billConfirm.createBill')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Appointments;
